/* eslint-disable jsx-a11y/alt-text */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";

// SVG Imports
import SvgComponent from "../SVGComponent";

import CardActionableButton from "../CommonViewAllCard/CardSubComponent/CardActionableButton";
import FileFormatMapper from "../FileFormatMapper";
import IconIndicator from "../IconIndicator";
import TickerAnimation from "../TickerAnimation";

// UTLIS Imports
import {
  GlobalConfiguration,
  ScreenSizeConfiguration,
} from "../../../utils/configurationProvider";
import { linkIdentifier } from "../../../utils/linkIdentifier";
import ICONS from "../../../assets/Icons";

const intercalate = (arr, sep) =>
  arr
    .reduce((a, v) => [...a, v, sep], [])
    .slice(
      GlobalConfiguration?.lengthCheckZero,
      GlobalConfiguration?.lengthCheckNegativeOne,
    );
/**
 * @typedef IconComponentTypeVariants
 * @type {("image" | "indicator")}
 * @typedef IconComponent
 * @type {object}
 * @property {String} [icon] - Icon to show
 * @property {String} bgColor - Background color of icon var only
 * @property {String} [title] - Text to show on hover
 * @property {Boolean} isDisabled
 * @property {String} imgColor - Color for icon if needed (var Only)
 * @property {IconComponentTypeVariants} type - Whether this is an image or an indicator. (if image please pass url in `icon`)
 * @property {OnClickEventHandler} [onClick] - If this is not undefined, this button will be made actionable.
 *
 */
/**
 * @typedef {((e: React.MouseEvent<HTMLButtonElement, MouseEvent>?) => void)} OnClickEventHandler
 *
 *
 *
 * @typedef FileOptions
 * @property {number} size - Size of file in bytes
 * @property {string} fileName - Name of file
 * @property {string} mimeType - MIME type of file
 * @property {string} url - URL of file
 *
 * @typedef Entry - Represents an entry in the card
 * @property {string} heading - Heading of this entry
 * @property {string | FileOptions} content - Content of this entry, can be string or a `FileOptions`
 * @property {("text" | "link" | "file" | "image" | "textwithicon" | "textWithButton")} type - Type of this entry, one of `text` `link` `file` `image`
 * @property {OnClickEventHandler} [onClick] - what happens when someone clicks on this entry
 * @property {OnClickEventHandler} [onUrlClick] - what happens when someone clicks on this url navigation
 * @property {boolean} isDisabled - is this entry disabled?
 * @property {IconComponent[]} [iconArray] - Array of what icons to show,
 * The first item of this array will be used when type = "textWithButton"
 * @property {String} [icon] - What icon to show on button, defaults to copy
 * @property {String} [iconColor] - what color icon to show defaults to primary-white
 *
 */
/**
 * @typedef {("fourth" | "third")} SizeVariant - Represents what proportion of the screen 1 column takes.
 * @param {object} props
 * @param {string} props.cardTitle - Title of the card.
 * @param {1 | 2 | 3 | 4} props.numCols -  Number of columns in the card, goes up to 4
 * @param {SizeVariant} props.variant - How much wide should 1 card be
 * @param {Entry[]} props.entries - A list of entries to show
 * @param {string} [props.icon] - An optional icon to show in the bottom right
 * @param {OnClickEventHandler} [props.onClick] - What happens when someone clicks on the whole card
 * @param {boolean} props.isDisabled - Is the entire card disabled.
 * @returns {React.JSX.Element}
 */
export default function CommonDetailCard({
  cardTitle,
  numCols,
  variant,
  entries,
  icon,
  onClick,
  isDisabled,
}) {
  let mql = window.matchMedia(
    `(width <= ${ScreenSizeConfiguration?.tabviewEnd}px)`,
  );

  const [isMobile, setIsMobile] = useState(mql.matches);
  useEffect(() => {
    const f = (mql) => {
      setIsMobile(mql.matches);
    };
    mql.addEventListener("change", f);
    return () => {
      mql.removeEventListener("change", f);
    };
  }, []);
  const colCount = isMobile ? GlobalConfiguration?.lengthCheckOne : numCols;
  /**
   *
   * @param {string} title
   * @returns {string}
   */
  const processTitle = (title) => {
    let result = [];
    const words = title.split(" ");
    for (let word of words) {
      const match = /^(.)(.*)$/.exec(word);
      const head = match[GlobalConfiguration?.lengthCheckOne].toUpperCase();
      const rest = match[GlobalConfiguration?.lengthCheckTwo];
      const w = `${head}${rest}`;
      result.push(w);
    }
    return result.join(" ");
  };

  let size;
  switch (variant) {
    case "third":
      switch (colCount) {
        case 1:
          size = "Qlg:w-details-card-md";
          break;
        case 2:
          size = "Qlg:w-details-card-lg";
          break;
        case 3:
          size = "Qlg:w-std-width";
          break;
        case 4:
          throw new Error(
            "Can't have more than 3 columns when variant = 'third'",
          );
        default:
          break;
      }
      break;
    case "fourth": {
      switch (colCount) {
        case 1:
          size = "Qlg:w-details-card-sm";
          break;
        case 2:
          size = "Qlg:w-details-card-4-2";
          break;
        case 3:
          size = "Qlg:w-details-card-4-3";
          break;
        case 4:
          size = "Qlg:w-std-width";
          break;
        default:
          break;
      }
      break;
    }
    default:
      break;
  }
  /**
   * @type {Entry[][]}
   */
  let bins = [];
  for (let i = 0; i < colCount; i++) bins[i] = [];
  for (let idx in entries) {
    bins[idx % colCount].push(entries[idx]);
  }
  let id = 0;
  bins = bins.map((entries, idx) => {
    const main = entries.map((entry, idx) => {
      id++;
      switch (entry.type) {
        case "link":
          return (
            <LinkRenderer
              content={entry.content}
              onClick={entry?.onClick}
              title={entry.heading}
              key={id}
            />
          );
        case "file":
          return (
            <FileRenderer
              content={entry.content}
              title={entry.heading}
              key={id}
            />
          );
        case "image":
          return (
            <ImageRenderer
              content={entry.content}
              title={entry.heading}
              key={id}
            />
          );
        case "textwithicon":
          return (
            <TextWithIconRenderer
              content={entry.content}
              onClick={entry?.onClick}
              title={entry.heading}
              disabled={entry?.isDisabled}
              key={id}
              iconArray={entry?.iconArray}
            />
          );
        case "textWithButton":
          return (
            <TextWithButtonRenderer
              content={entry.content}
              title={entry.heading}
              iconArray={entry.iconArray}
              key={id}
            />
          );
        case "text":
        default:
          return (
            <TextRenderer
              content={entry.content}
              onClick={entry?.onClick}
              title={entry.heading}
              disabled={entry?.isDisabled}
              key={id}
            />
          );
      }
    });
    return (
      <>
        <div
          className={`cdc-entry-desktop Qlg:flex hidden flex-1 w-width-base h-full flex-col gap-gap-base justify-center px-padding-sm ${idx !== colCount - GlobalConfiguration?.lengthCheckOne && "border-r border-primary-bg"}`}
        >
          {intercalate(main, <hr className="mx-margin-sm border-primary-bg" />)}
        </div>
        <div
          className={`cdc-entry-mobile flex Qlg:hidden flex-1 w-full h-full flex-col gap-gap-base justify-start px-padding-sm ${idx !== colCount - GlobalConfiguration?.lengthCheckOne && "border-b border-primary-bg"}`}
        >
          {intercalate(main, <hr className="border-primary-bg mx-margin-sm" />)}
          {/* {main} */}
        </div>
      </>
    );
  });
  return (
    <>
      <div
        style={{
          height: "fit-content",
        }}
        onClick={(e) => {
          if (!onClick) return;
          if (e.target.tagName === "A") return false;
          onClick();
        }}
        className={`${size}  ${onClick && !isDisabled ? "global-hover-white relative z-z-index-base" : ""} ${isDisabled ? "cursor-not-allowed" : ""} w-full bg-primary-card rounded-border-radius-md relative flex flex-col gap-gap-sm `}
      >
        <div
          className={`cdc-title pt-padding-md px-padding-md font-semibold text-text-primary text-font-sm flex justify-between items-center ${isDisabled ? "opacity-light-opacity" : ""} gap-gap-sm`}
        >
          <span className="relative min-w-min-width-base w-full">
            <TickerAnimation
              text={processTitle(cardTitle)}
              fontSize={"md"}
            />
          </span>
          {/* <span>{processTitle(cardTitle)}</span> */}
          <div className={` ${onClick ? "flex" : "hidden"}`}>
            <SvgComponent
              url={ICONS?.ARROW_RIGHT_WHITE}
              width="var(--icon-sm)"
              height="var(--icon-sm)"
              color="var(--text-primary)"
            />
          </div>
        </div>

        <div className="flex Qlg:flex-row flex-col pb-padding-sm items-start ">
          {bins}
        </div>
        <div className="absolute bottom-bottom-base right-right-base">
          {icon && (
            <div className="bg-text-primary rounded-tl-border-radius-md rounded-br-border-radius-md p-padding-sm">
              <div className="w-icon-md h-icon-md">
                <img
                  src={icon}
                  alt=""
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
/**
 * @param {Object} props
 * @param {string} props.title
 * @param {FileOptions} props.content
 * @param {OnClickEventHandler} props.onClick,
 * @param {boolean} props.disabled,
 * @param {IconComponent[]} props.iconArray,
 *
 *
 * @returns {React.JSX.Element}
 */
function TextWithIconRenderer({
  title,
  onClick,
  content,
  iconArray,
  disabled,
}) {
  return (
    <>
      <div
        onClick={(e) => {
          if (!onClick) return;
          if (e.target.tagName === "A") return false;
          onClick();
        }}
        className={`flex  flex-col gap-gap-xs p-padding-sm w-full z-z-index-base relative ${
          onClick && !disabled
            ? "global-hover-white hover:rounded-border-radius-sm cursor-pointer"
            : disabled
              ? "cursor-not-allowed opacity-light-opacity"
              : onclick
                ? "cursor-pointer"
                : ""
        }`}
      >
        <div className="text-text-primary text-font-sm">
          <div className="flex flex-row gap-gap-sm w-full justify-between items-center">
            <div className="flex flex-row justify-start gap-gap-sm">
              <span className="whitespace-normal break-words">
                {linkIdentifier(content)}
              </span>
              <div className="flex flex-row justify-start h-auto gap-gap-sm items-center">
                {intercalate(
                  iconArray?.map((icon, idx) => {
                    if (icon.type === "indicator") {
                      return (
                        <>
                          <IconIndicator
                            bgColor={icon.bgColor}
                            title={icon.title}
                            borderColor={icon.imgColor}
                          />
                        </>
                      );
                    } else
                      return (
                        <CardActionableButton
                          key={idx}
                          handleFunction={icon?.onClick}
                          icon={icon?.icon}
                          isDisabled={icon?.isDisabled}
                          title={icon?.title}
                          bgColor={icon?.bgColor}
                          imgColor={icon?.imgColor}
                        />
                      );
                  }),
                  <div className="h-[-webkit-fill-available] border-r border-primary-bg"></div>,
                )}
              </div>
            </div>

            {!!onClick && (
              <SvgComponent
                color="var(--text-primary)"
                height="var(--icon-sm)"
                width="var(--icon-sm)"
                url={ICONS?.ARROW_RIGHT_WHITE}
                alt="redirect"
              />
            )}
          </div>
        </div>
        <div className="text-text-secondary text-font-xs">{title}</div>
      </div>
    </>
  );
}
function TextRenderer({ title, content, onClick, disabled }) {
  return (
    <>
      <div
        onClick={(e) => {
          if (!onClick) return;
          if (e.target.tagName === "A") return false;
          onClick();
        }}
        className={`flex  flex-col gap-gap-xs p-padding-sm w-full z-z-index-base relative ${
          onClick && !disabled
            ? "global-hover-white hover:rounded-border-radius-sm cursor-pointer"
            : disabled
              ? "cursor-not-allowed opacity-light-opacity"
              : onclick
                ? "cursor-pointer"
                : ""
        }`}
      >
        <div className="text-text-primary text-font-sm">
          <div className="flex flex-row gap-gap-sm w-full justify-between items-center">
            <span className="break-words whitespace-pre-line">
              {linkIdentifier(content)}
            </span>
            {!!onClick && (
              <SvgComponent
                color="var(--text-primary)"
                height="var(--icon-sm)"
                width="var(--icon-sm)"
                url={ICONS?.ARROW_RIGHT_WHITE}
                alt="redirect"
              />
            )}
          </div>
        </div>
        <div className="text-text-secondary text-font-xs">{title}</div>
      </div>
    </>
  );
}

function LinkRenderer({ title, content }) {
  const [url, setUrl] = useState(ICONS?.LOADER_GIF);
  const getFavicon = (url) => {
    const regex = /^(?:(?:https?:)?\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:/\n?]+)/gim;

    const match = regex.exec(url);
    const res = `https://www.google.com/s2/favicons?domain=${match[0]}&sz=64`;
    const test = new Image();
    test.onload = () => {
      setUrl(res);
    };
    test.onerror = () => {
      setUrl(ICONS?.LINK_WHITE);
    };
    test.src = res;
  };
  getFavicon(content);
  return (
    <>
      <div className="flex  flex-col gap-gap-xs w-full p-padding-sm">
        <div className="text-font-xs text-text-secondary">{title}</div>
        <span className="bg-primary-bg rounded-border-radius-sm p-padding-sm flex flex-col gap-gap-xs w-auto">
          <span className="bg-primary-nav w-full flex flex-row gap-gap-sm justify-start items-center rounded-border-radius-md p-padding-sm">
            <div className="flex-shrink-0  rounded-border-radius-sm flex flex-row justify-center items-center bg-text-primary  w-icon-lg h-icon-lg">
              <img
                className="w-icon-md h-icon-md"
                src={url}
              ></img>
            </div>
            <span className="flex whitespace-normal break-all flex-row items-center text-font-xxs text-text-secondary truncate line-clamp-1 pr-padding-sm">
              <TickerAnimation text={content} />
            </span>
          </span>
          <a
            href={content}
            target="_blank"
            rel="noopener noreferrer"
            className="underline line-clamp-2 text-font-xxs whitespace-normal break-all truncate text-pending"
          >
            {content}
          </a>
        </span>
      </div>
    </>
  );
}

/**
 * @param {Object} props
 * @param {string} props.title
 * @param {FileOptions} props.content
 * @param {Pick<IconComponent, "imgColor"  | "icon"  | "onClick" |  "title">[]} props.iconArray
 *
 * @returns {React.JSX.Element}
 */
function TextWithButtonRenderer({ title, content, iconArray }) {
  const { imgColor: iconColor, icon, onClick, title: tooltip } = iconArray[0];
  return (
    <>
      <div
        className={`flex  flex-row items-center justify-between gap-gap-xs p-padding-sm w-full z-z-index-base relative `}
      >
        <div className="flex flex-col gap-gap-xs">
          <div className="text-text-primary text-font-sm">
            <div className="flex flex-row gap-gap-sm w-full justify-between items-center">
              <span className="break-words whitespace-pre-line">
                {linkIdentifier(content)}
              </span>
            </div>
          </div>
          <div className="text-text-secondary text-font-xs">{title}</div>
        </div>
        {!!onClick && (
          <CardActionableButton
            btnType="button"
            icon={icon || ICONS?.COPY}
            title={tooltip}
            handleFunction={onClick}
            bgColor="var(--primary-nav-act)"
            hoverOption="white"
            imgColor={iconColor || ""}
          />
        )}
      </div>
    </>
  );
}

/**
 * @param {Object} props
 * @param {string} props.title
 * @param {FileOptions} props.content
 * @returns {React.JSX.Element}
 */
function FileRenderer({ title, content }) {
  const [imgSrc, setImgSrc] = useState(
    content.url,
    // FileFormatMapper(content.fileName?.split(".").pop().toLowerCase(), true),
  );

  const [attemptedErrorRecovery, setAttemptedErrorRecovery] = useState(false);
  return (
    <>
      <div className="flex flex-col gap-gap-xs w-full p-padding-sm">
        <div className="text-font-xs text-text-secondary">{title}</div>
        <div
          onClick={(e) => window.open(content.url, "_blank").focus()}
          className="bg-primary-bg rounded-border-radius-sm p-padding-sm flex flex-row items-center gap-gap-sm w-auto cursor-pointer"
        >
          <div className="w-icon-lg h-icon-lg flex-shrink-0">
            <img
              className="w-icon-lg h-icon-lg"
              src={imgSrc}
              onError={function (e) {
                if (attemptedErrorRecovery) {
                  if (e.target.src !== ICONS?.DEFAULT_FILE)
                    setImgSrc(ICONS?.DEFAULT_FILE);
                } else {
                  setAttemptedErrorRecovery(true);
                  let f = FileFormatMapper(
                    content.fileName?.split(".").pop().toLowerCase(),
                    true,
                  );
                  setImgSrc(f);
                  return false;
                }
              }}
              alt=""
            />
          </div>
          <div className="flex flex-col gap-gap-xs">
            <span className="text-font-xs text-text-primary break-all">
              {content.fileName}
            </span>
            <div className="text-font-xxs text-text-secondary">
              {formatBytes(content.size)} .
              {content.fileName?.split(".").pop().toUpperCase()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
/**
 * @param {Object} props
 * @param {string} props.title
 * @param {FileOptions} props.content
 * @returns {React.JSX.Element}
 */
function ImageRenderer({ title, content }) {
  const [imgSrc, setImgSrc] = useState(content.url);
  const [attemptedErrorRecovery, setAttemptedErrorRecovery] = useState(false);
  const fileFormat = content.mimeType.split("/").pop().toUpperCase();

  return (
    <>
      <div className="flex flex-col gap-gap-xs w-full p-padding-sm">
        <div className="text-font-xs text-text-secondary">{title}</div>
        <div
          onClick={content?.onUrlClick}
          className="bg-primary-bg rounded-border-radius-sm p-padding-sm flex flex-row items-center gap-gap-sm w-auto cursor-pointer"
        >
          <div className="w-icon-lg h-icon-lg  flex-shrink-0">
            <img
              className="w-icon-lg h-icon-lg rounded-border-radius-sm flex-shrink-0"
              src={imgSrc}
              alt=""
              onError={function (e) {
                if (attemptedErrorRecovery) {
                  if (e.target.src !== ICONS?.DEFAULT_FILE)
                    setImgSrc(ICONS?.DEFAULT_FILE);
                } else {
                  setAttemptedErrorRecovery(true);
                  let f = FileFormatMapper(
                    content.fileName?.split(".").pop().toLowerCase(),
                    true,
                  );
                  setImgSrc(f);
                  return false;
                }
              }}
            />
          </div>
          <div className="flex flex-col gap-gap-xs">
            <span className="text-font-xs text-text-primary break-all">
              {content.fileName}
            </span>
            <div className="text-font-xxs text-text-secondary">
              {formatBytes(content.size)} . {fileFormat}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function formatBytes(bytes, decimals = 2) {
  if (bytes === GlobalConfiguration?.lengthCheckZero)
    return `${GlobalConfiguration?.lengthCheckZero} Bytes`;

  const k = ScreenSizeConfiguration?.tabviewEnd;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  const formatted = parseFloat((bytes / Math.pow(k, i)).toFixed(decimals));
  return `${formatted} ${sizes[i]}`;
}
